import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useApproveImportMutation, useGetExtractedPatientsQuery } from '../store/hooks';
import { TARGET_FIELD_LABELS } from '../types';

// =============================================================================
// Types
// =============================================================================

interface ExtractedPatientsReviewProps {
  sessionId: string;
  onCancel: () => void;
}

interface ColumnDef {
  key: string;
  label: string;
  minWidth: number;
}

// =============================================================================
// Column Configuration
// =============================================================================

const SKELETON_ROW_COUNT = 5;
const PAGE_LIMIT = 100;

/** Keys that are metadata, not patient data columns */
const METADATA_KEYS = new Set(['extracted_patient_id', 'import_status', 'match_status']);

function getMinWidthForField(field: string): number {
  switch (field) {
    case 'address':
    case 'email':
    case 'emergency_contact_name':
    case 'insurance_policy_number':
      return 180;
    case 'emergency_contact_relationship':
      return 200;
    case 'first_name':
    case 'last_name':
    case 'preferred_name':
    case 'payer_name':
      return 140;
    case 'dob':
    case 'gender':
    case 'sex':
    case 'pronouns':
    case 'race':
    case 'zip':
    case 'state':
      return 100;
    default:
      return 120;
  }
}

/** Format a snake_case key into a Title Case label */
function formatFieldLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Use a known label if available, otherwise format the raw key */
function getLabelForField(key: string): string {
  return (TARGET_FIELD_LABELS as Record<string, string>)[key] ?? formatFieldLabel(key);
}

// =============================================================================
// Helpers
// =============================================================================

const stickyColumnSx = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: 'background.paper',
} as const;

const stickyHeaderColumnSx = {
  ...stickyColumnSx,
  zIndex: 3,
} as const;

// =============================================================================
// Component
// =============================================================================

export const ExtractedPatientsReview = ({ sessionId, onCancel }: ExtractedPatientsReviewProps) => {
  const theme = useTheme();
  const { patientImport } = useEventTracking();
  const [offset, setOffset] = useState(0);
  const reviewViewedRef = useRef(false);

  const {
    data,
    isLoading,
    isFetching,
    error: fetchError,
  } = useGetExtractedPatientsQuery({ sessionId, limit: PAGE_LIMIT, offset });

  const [approveImport, { isLoading: isApproving }] = useApproveImportMutation();

  const patients = useMemo(
    () => data?.patients ?? [],
    [data?.patients],
  );
  const totalCount = data?.total_count ?? 0;
  const hasMore = data?.has_more ?? false;

  // Track review viewed once when data first loads
  useEffect(() => {
    if (totalCount > 0 && !reviewViewedRef.current) {
      reviewViewedRef.current = true;
      patientImport.reviewViewed({
        session_id: sessionId,
        total_patients: totalCount,
      });
    }
  }, [totalCount, sessionId, patientImport]);

  // Derive all data columns from patient keys (preserving first-seen order)
  const allColumns = useMemo<ColumnDef[]>(() => {
    if (patients.length === 0) return [];

    const keysInOrder: string[] = [];
    const keysSeen = new Set<string>();

    for (const patient of patients) {
      for (const key of Object.keys(patient)) {
        if (!METADATA_KEYS.has(key) && !keysSeen.has(key)) {
          keysSeen.add(key);
          keysInOrder.push(key);
        }
      }
    }

    return keysInOrder.map((key) => ({
      key,
      label: getLabelForField(key),
      minWidth: getMinWidthForField(key),
    }));
  }, [patients]);

  const handleApprove = async () => {
    patientImport.approved({
      session_id: sessionId,
      total_patients: totalCount,
    });
    await approveImport({ sessionId });
  };

  const handleLoadMore = () => {
    setOffset((prev) => prev + PAGE_LIMIT);
  };

  // ---------------------------------------------------------------------------
  // Loading State
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={280} height={32} />
          <Skeleton variant="text" width={200} height={24} />
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {['#', 'First Name', 'Last Name', 'DOB', 'Gender', 'Email', 'Phone', 'Status'].map(
                  (col) => (
                    <TableCell key={col}>
                      <Skeleton variant="text" width={60} />
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: 8 }).map((_, cellIdx) => (
                    <TableCell key={cellIdx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Error State
  // ---------------------------------------------------------------------------

  if (fetchError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load extracted patients. Please try again.
      </Alert>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Review Extracted Patients
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {totalCount} patients ready to import
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto', mb: 2 }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 'max-content' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 56, ...stickyHeaderColumnSx }}>
                #
              </TableCell>
              {allColumns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 600,
                    minWidth: col.minWidth,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={allColumns.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No patients found in this import session.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              patients.map((patient, idx) => (
                <TableRow key={patient.extracted_patient_id} hover>
                  <TableCell sx={stickyColumnSx}>{offset + idx + 1}</TableCell>
                  {allColumns.map((col) => {
                    const value = (patient as Record<string, string | null | undefined>)[col.key];
                    return (
                      <TableCell key={col.key} sx={{ whiteSpace: 'nowrap' }}>
                        {value ?? '\u2014'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Load More / Has More indicator */}
      {hasMore && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button size="small" onClick={handleLoadMore} disabled={isFetching}>
            Load More
          </Button>
          <Typography variant="caption" color="text.secondary">
            Showing first {Math.min(offset + PAGE_LIMIT, totalCount)} of {totalCount}
          </Typography>
        </Box>
      )}

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button variant="outlined" onClick={onCancel} disabled={isApproving}>
          Cancel Import
        </Button>
        <Button
          variant="contained"
          onClick={handleApprove}
          disabled={isApproving || patients.length === 0}
        >
          {isApproving ? 'Importing...' : `Import All ${totalCount} Patients`}
        </Button>
      </Box>
    </Box>
  );
};
